import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';

/**
 * A token minted for an application, held for the life of one CLI session.
 *
 * The credential behind it is revoked the moment the token is issued, so what
 * sits on disk cannot mint anything — it is a bearer token that expires, exactly
 * like the human one beside it. Nothing here is long-lived.
 */
const SESSION_PATH = path.join(os.homedir(), '.sortlist', 'application-session.json');

export interface ApplicationSession {
  accessToken: string;
  applicationId: string;
  expiresAt: number;
}

export function loadApplicationSession(): ApplicationSession | null {
  try {
    const session: ApplicationSession = JSON.parse(fs.readFileSync(SESSION_PATH, 'utf-8'));
    if (Date.now() >= session.expiresAt) return null;

    return session;
  } catch {
    return null;
  }
}

export function saveApplicationSession(session: ApplicationSession): void {
  fs.mkdirSync(path.dirname(SESSION_PATH), { recursive: true });
  fs.writeFileSync(SESSION_PATH, JSON.stringify(session, null, 2) + '\n', { mode: 0o600 });
}

export function clearApplicationSession(): boolean {
  try {
    fs.unlinkSync(SESSION_PATH);
    return true;
  } catch {
    return false;
  }
}

/** True when a session file exists but its token has run out. */
export function applicationSessionExpired(): boolean {
  return fs.existsSync(SESSION_PATH) && loadApplicationSession() === null;
}

/**
 * True when the CLI is authenticating as an application rather than as a person.
 *
 * Worth asking before explaining a refusal: Hermes rejects the tokens it mints on
 * its own management API, so `companies`, `applications` and every `create*`
 * mutation fail while an application credential is in play — and the router
 * flattens that refusal to "Unexpected error", which explains nothing.
 */
export function actingAsApplication(): boolean {
  if (process.env.SORTLIST_CLIENT_ID && process.env.SORTLIST_CLIENT_SECRET) return true;

  return loadApplicationSession() !== null;
}
